import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View, Text, Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { t } from '../../i18n/translations';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { formatCount } from '../../utils/numberFormatter';
import { useLanguageStore } from '../quiz-core/languageStore';

const shellColors = {
  panel: 'rgba(8, 11, 36, 0.94)',
  panelHigh: 'rgba(255, 247, 218, 0.08)',
  gold: '#F8C84E',
  ivory: '#FFF7DA',
  muted: '#D9D2C2',
  green: '#153F27',
  line: 'rgba(248, 200, 78, 0.38)',
};

interface FriendsGroup {
  id: string;
  name: string;
  group_code?: string;
}

interface FriendsCardProps {
  group: FriendsGroup;
  members: Array<{ user_id?: string }>;
  userRole: 'owner' | 'admin' | 'member' | string;
  onShareLink: () => void;
  onShareCode: () => void;
  onLeaveGroup: () => void;
  onShowMembers: () => void;
  onGoToLobby: (group: FriendsGroup) => void;
}

const FriendsCard: React.FC<FriendsCardProps> = ({
  group,
  members,
  userRole,
  onShareLink,
  onShareCode,
  onLeaveGroup,
  onShowMembers,
  onGoToLobby,
}) => {
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  const { language } = useLanguageStore();
  const normalizedName = group.name?.trim().toLowerCase();
  const displayName = !normalizedName || normalizedName === 'new' || normalizedName === 'new group'
    ? `${t('familiesFriends.groupTypeFriends', language)} ${t('familiesFriends.group', language)}`
    : group.name;

  const getRoleText = (role: string) => {
    switch(role) {
      case 'owner': return t('familiesFriends.roleOwner', language);
      case 'admin': return t('familiesFriends.roleAdmin', language);
      case 'member': return t('familiesFriends.roleMember', language);
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  return (
    <View style={[premiumStyles.premiumCard, { backgroundColor: shellColors.panel, borderColor: shellColors.line }]}>
      <View style={premiumStyles.headerSection}>
        <View style={premiumStyles.headerContent}>
          <View style={premiumStyles.titleRow}>
            <Text style={[premiumStyles.groupTitle, { color: shellColors.ivory }]} numberOfLines={2}>{displayName}</Text>
          </View>

          <View style={premiumStyles.metaRow}>
            {group.group_code && (
              <View style={premiumStyles.codeBadge}>
                <Text selectable style={premiumStyles.codeText}>
                  {group.group_code}
                </Text>
              </View>
            )}
          </View>

          <View style={premiumStyles.statsRow}>
            <View style={premiumStyles.statItem}>
              <Text style={[premiumStyles.statNumber, { color: shellColors.ivory }]}>{formatCount(members.length, language)}</Text>
              <Text style={[premiumStyles.statLabel, { color: shellColors.muted }]}>{t('familiesFriends.membersLabel', language)}</Text>
            </View>
            <View style={[premiumStyles.statDivider, { backgroundColor: shellColors.line }]} />
            <View style={premiumStyles.statItem}>
              <Ionicons
                name={userRole === 'owner' ? 'shield-checkmark' : userRole === 'admin' ? 'star' : 'person'}
                size={18}
                color={shellColors.gold}
                style={{ marginBottom: 4 }}
              />
              <Text style={[premiumStyles.statLabel, { color: shellColors.muted }]}>{getRoleText(userRole)}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={premiumStyles.actionSection}>
        <PrimaryButton
          title={t('familiesFriends.enterFriendsLobby', language)}
          onPress={() => onGoToLobby(group)}
          style={premiumStyles.lobbyButton}
        />
        <View style={premiumStyles.secondaryActionRow}>
          {isAdmin && (
            <>
              <TouchableOpacity
                onPress={onShareLink}
                style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
              >
                <Ionicons name="link-outline" size={14} color={shellColors.muted} />
                <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
                  {t('familiesFriends.shareLink', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={onShareCode}
                style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
              >
                <Ionicons name="copy-outline" size={14} color={shellColors.muted} />
                <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
                  {t('familiesFriends.copyCode', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
                </Text>
              </TouchableOpacity>
            </>
          )}
          <TouchableOpacity
            onPress={onShowMembers}
            style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
          >
            <Ionicons name="people-outline" size={14} color={shellColors.muted} />
            <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
              {t('familiesFriends.viewMembers', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onLeaveGroup}
            style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
          >
            <Ionicons name="exit-outline" size={14} color={shellColors.muted} />
            <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
              {t('familiesFriends.leaveGroup', language)}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// Digital Sanctuary Design System Styles
const premiumStyles = StyleSheet.create({
  premiumCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 16px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  headerSection: {
    padding: 20,
    paddingBottom: 16,
  },
  headerContent: {
    flex: 1,
  },
  titleRow: {
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  groupTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1D1A23',
    marginRight: 12,
  },
  codeBadge: {
    backgroundColor: shellColors.green,
    borderWidth: 1,
    borderColor: shellColors.line,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    ...Platform.select({
      ios: {
        shadowColor: '#10B981',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 2px 8px 0px rgba(16, 185, 129, 0.3)',
      },
    }),
  },
  codeText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.5,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    alignItems: 'center',
    minWidth: 76,
  },
  statNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1D1A23',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#777587',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 16,
  },
  actionSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 4,
  },
  lobbyButton: {
    marginTop: 0,
  },
  secondaryActionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  secondaryActionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 7,
    gap: 6,
    opacity: 0.92,
  },
  secondaryActionText: {
    fontSize: 11,
    fontWeight: '600',
  },
});

export default FriendsCard;
